import { FC, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Footer from '../../../../../../components/Footer/Footer';
import { IDailyLookBook } from '../../../../services/api/lookBook/lookbook-model-definitions.ts';
import { useResize } from '../../../../hooks';
import { STORY_ROUTE } from '../../../../router/router';
import StoriesSkeletonList from './components/StoriesSkeletonList/StoriesSkeletonList';
import { getWithOutSpecialChars } from '../../../../utils';
import SingleCard from '../../components/SingleCard/SingleCard';
import { getSSRTranslation } from '@/i18n.ts';
import { CommonPageDataContext } from '../../../../ContextWrapper/WithInterest/CommonPageDataContext.ts';

import './index.scss';
import { useQuery } from '@tanstack/react-query';
import { fetchStories, STORIES_QUERY_KEY, } from '@moment/net/classic-fotoscapes/fetch-stories.ts';
import { GamAd } from '@moment/components/GamAd/GamAd.tsx';
import { AD_SIZE } from '@moment/components/GalleryAd/ad-constants.ts';
import { AdRenderer } from '@moment/components/AdRenderer/AdRenderer.tsx';
import {
  DISPLAY_AD_DESKTOP_AD_MAP_DATA,
  DISPLAY_AD_MOBILE_AD_MAP_DATA,
  DISPLAY_AD_TABLED_AD_MAP_DATA,
  DISPLAY_AD_TABLED_SMALL_AD_MAP_DATA,
} from '@moment/components/DisplayAd/constants.ts';

const Stories: FC = () => {
  const { isDesktop, isTabled, isMobile } = useResize();
  const { interests } = useContext(CommonPageDataContext);
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const { data, isLoading } = useQuery({
    queryKey: [lang, STORIES_QUERY_KEY],
    queryFn: fetchStories,
  });

  const storyList = useMemo(() => {
    return data?.items || [];
  }, [data]);

  const renderList = useMemo(() => {
    let rest = storyList.length;

    if (isDesktop) {
      rest = -storyList.length % 3 || storyList.length;
    }

    if (isTabled) {
      rest = -storyList.length % 2 || storyList.length;
    }

    return storyList.slice(0, rest);
  }, [storyList, isDesktop, isTabled]);

  const firstListLength = useMemo(() => {
    if (isMobile) {
      return 1;
    }

    if (isTabled) {
      return 4;
    }

    return 6;
  }, [isTabled, isMobile]);

  const firstSectionList = useMemo(() => {
    return renderList.slice(0, firstListLength);
  }, [renderList, firstListLength]);

  const secondSectionList = useMemo(() => {
    return [...renderList.slice(firstListLength)];
  }, [renderList, firstListLength]);

  const getInterestName = useCallback(
    (interestUID: string) => {
      const name = interests.interestForId(interestUID)?.name;

      if (name) {
        return getSSRTranslation(name, i18n);
      }

      return '';
    },
    [interests]
  );

  const renderItem = (data: IDailyLookBook) => {
    const { title, summary, previews, uid, interests } = data;
    const interestName = getInterestName(interests[0]);

    return (
      <div className="stories__card-wrapper" key={uid}>
        <SingleCard
          uid={uid}
          interestId={interests[0]}
          link={STORY_ROUTE.path
            .replace(':uid', uid)
            .replace(':title', getWithOutSpecialChars(title.en))}
          title={getSSRTranslation(title, i18n)}
          description={getSSRTranslation(summary, i18n)}
          descriptionLineCount={3}
          imgList={previews}
          interest={interestName}
        />
      </div>
    );
  };

  const size = [
    ...DISPLAY_AD_DESKTOP_AD_MAP_DATA.adSizeList,
    ...DISPLAY_AD_TABLED_AD_MAP_DATA.adSizeList,
    ...DISPLAY_AD_TABLED_SMALL_AD_MAP_DATA.adSizeList,
    ...DISPLAY_AD_MOBILE_AD_MAP_DATA.adSizeList,
  ];

  const responsiveSize = [
    DISPLAY_AD_DESKTOP_AD_MAP_DATA,
    DISPLAY_AD_TABLED_AD_MAP_DATA,
    DISPLAY_AD_TABLED_SMALL_AD_MAP_DATA,
    DISPLAY_AD_MOBILE_AD_MAP_DATA,
  ];

  // Create GAM ad component
  const adUnitId = 'stories';
  const gamAd = (
    <GamAd
      key={`${adUnitId}-gam`}
      adUnitId={adUnitId}
      adType="top"
      size={size}
      responsiveSize={responsiveSize}
      showBanner={true}
      containerClassName="ad--block"
      bannerClassName="ad--title"
      adBlockClassName="feed__ad"
    />
  );

  return (
    <div className="stories">
      <h1 className="stories__title">{t('fotoSCAPESSExclusive')}</h1>

      <h2 className="stories__subtitle paragraph--l">{t('diveDeep')}</h2>

      {firstSectionList?.length ? (
        <div className="stories__list">{firstSectionList.map(renderItem)}</div>
      ) : (
        <>
          {isLoading ? (
            <StoriesSkeletonList />
          ) : (
            <div className="stories__error">
              {t('noStories', 'No stories found')}
            </div>
          )}
        </>
      )}

      <div className="stories__ad-wrapper">
        <AdRenderer
          placement="top"
          adUnitId={adUnitId}
          GamComponent={gamAd}
          dtxAdSize={[AD_SIZE.middle]}
          containerClassName="ad--block"
          bannerClassName="ad--title"
          adBlockClassName="feed__ad"
        />
      </div>

      {secondSectionList?.length ? (
        <div className="stories__list">{secondSectionList.map(renderItem)}</div>
      ) : null}
      <Footer />
    </div>
  );
};

export default Stories;
